import { ChatModel, ChatType } from '../chat/chat.model';
import { RequestModel, RequestStatus, RequestType } from './request.model';
import { TranslatorProvider } from 'src/providers/translator.provider';
import { ChatItemModel, ChatItemStyle, ChatItemType } from '../chat/chat-item.model';
import { RequestStatusDictionary } from 'src/providers/request.provider';
import { parseUtcDate } from 'src/shared/utils/date-utils';

export class RequestApplicationModel extends ChatModel {
  constructor(
    id: string,
    title: string,
    public createdByUserId: string,
    public databaseIdentifier: string,
    public fieldGroupId: string,
    public createdAt: Date,
    isRead: boolean,
    public requests: RequestModel[]
  ) {
    super(id, title, isRead, ChatType.Requests, requests);
  }

  static toChatModel(
    application: RequestApplicationModel,
    translator: TranslatorProvider
  ): ChatModel {
    return {
      id: application.id,
      isRead: application.isRead,
      items: application.requests.map((request) => {
        const item: ChatItemModel = {
          createdAt: parseUtcDate(request.createdAt),
          username: request.username,
          fromClient: request.fromClient,
        };

        const message = this.convertApplicationToMessage(application.title, request, translator);

        if (
          request.status === RequestStatus.Approved ||
          request.status === RequestStatus.Rejected
        ) {
          // notice
          item.icon = RequestStatusDictionary[request.status];
          item.title = message;
          item.message = request.message;
          item.itemType = ChatItemType.Notice;
          item.style = this.getStyle(request.status);
        } else {
          // message
          item.title = this.getMessageTitle(
            request.message,
            request.status,
            request.type,
            translator
          );
          item.message = message;
          item.itemType = ChatItemType.Message;
        }

        return item;
      }),
      title: application.title,
      type: ChatType.Requests,
    } as ChatModel;
  }

  static getMessagePreview(applicationTitle: string, request: RequestModel, translator: any) {
    if (!request) return '';

    const msg = this.convertApplicationToMessage(applicationTitle, request, translator);

    return msg || this.getMessageTitle(request.message, request.status, request.type, translator);
  }

  private static convertApplicationToMessage(
    applicationTitle: string,
    request: RequestModel,
    translator: TranslatorProvider
  ): string {
    switch (request.status) {
      case RequestStatus.Approved:
        return `${applicationTitle} ${translator.dictionary.was_approved}`;
      case RequestStatus.Rejected:
        return `${applicationTitle} ${translator.dictionary.was_rejected}`;
      case RequestStatus.Requested:
        if (request.type === RequestType.Message) {
          return request.message;
        }
        return this.convertFieldsValueToMessage(request.fieldsValue);
      case RequestStatus.Feedback:
        return request.message;
    }
  }

  private static convertFieldsValueToMessage(fieldsValue: string) {
    return fieldsValue ? fieldsValue.replace(/;/gi, '\n') : '';
  }

  private static getStyle(status: RequestStatus): ChatItemStyle {
    switch (status) {
      case RequestStatus.Rejected:
        return ChatItemStyle.Danger;
      case RequestStatus.Feedback:
        return ChatItemStyle.Warning;
      case RequestStatus.Approved:
      default:
        return ChatItemStyle.Success;
    }
  }

  private static getMessageTitle(
    message: string,
    status: RequestStatus,
    type: RequestType,
    translator: TranslatorProvider
  ) {
    let title = '';

    if (
      status === RequestStatus.Requested &&
      (type === RequestType.Request || type === RequestType.UpdateRequest)
    ) {
      title =
        type === RequestType.Request
          ? translator.dictionary.Request_created
          : translator.dictionary.Request_updated;

      if (message) title += ':';
    }

    return title;
  }
}
